/**
 * Chats (conversations) resource — dashboard-side (authenticated).
 *
 * Thin typed wrappers over {@link endpoints.chats}. Business behavior (pagination
 * cursors, optimistic reconcile) lives in the app stores; this layer only shapes
 * requests/responses per the ARCHITECTURE §2 `chats.py` router.
 */

/** One co-browse capture of the visitor's page (html is null on unchanged-DOM polls). */
export interface CobrowseFrame {
  seq: number;
  html: string | null;
  scrollX: number;
  scrollY: number;
  viewportW: number;
  viewportH: number;
  cursorX?: number | null;
  cursorY?: number | null;
  pageUrl?: string | null;
}

import type { HttpClient } from '../httpClient';
import { endpoints } from '../endpoints';
import type { ChatNote, ChatSession, Message, MessageMedia, SessionStatus } from '../../types';

/** Query params for listing chats in the dashboard. */
export interface ListChatsParams {
  /** One or more statuses; serialized as repeated `?status=` params (FastAPI `list[...]`). */
  status?: SessionStatus[];
  /** Filter to chats claimed by this agent. */
  claimedByAgentId?: string;
  cursor?: string;
  limit?: number;
  offset?: number;
}

/** Body for sending a message from the dashboard (with dual-send targeting). */
export interface SendMessageBody {
  body?: string;
  clientMsgId: string;
  /** Which of the session's `activeChannels` to send on; defaults to all. */
  channels?: ChatSession['activeChannels'];
  /** Attachments (each from a prior `uploadMedia` call). */
  media?: MessageMedia[];
  /** Staff-only note ("send as note"): stored in the transcript, never sent to the visitor. */
  internal?: boolean;
}

export class ChatsResource {
  constructor(private readonly http: HttpClient) {}

  list(params: ListChatsParams = {}): Promise<{ items: ChatSession[]; cursor: string | null }> {
    return this.http.get(endpoints.chats.list(), { query: { ...params } });
  }

  get(sessionId: string): Promise<ChatSession> {
    return this.http.get(endpoints.chats.detail(sessionId));
  }

  messages(
    sessionId: string,
    params: { cursor?: string; limit?: number } = {},
  ): Promise<{ items: Message[]; cursor: string | null }> {
    return this.http.get(endpoints.chats.messages(sessionId), { query: { ...params } });
  }

  send(sessionId: string, body: SendMessageBody): Promise<Message[]> {
    // One Message per channel is returned (dual-send, ARCHITECTURE §5).
    return this.http.post(endpoints.chats.messages(sessionId), body);
  }

  /**
   * Upload an attachment (image/PDF); returns a descriptor to pass in `send({ media })`.
   *
   * `sessionId` files it into that conversation's storage folder; `scope: 'common'` files org
   * branding images into the shared folder. Omit both for an unattached upload.
   */
  uploadMedia(
    file: File,
    opts?: { sessionId?: string; scope?: 'common' },
  ): Promise<MessageMedia> {
    const fd = new FormData();
    fd.append('file', file);
    if (opts?.sessionId) fd.append('session_id', opts.sessionId);
    if (opts?.scope) fd.append('scope', opts.scope);
    return this.http.post(endpoints.media.upload(), undefined, { formData: fd });
  }

  /** Atomic first-wins claim (ARCHITECTURE §5, `claim_service`). */
  claim(sessionId: string): Promise<ChatSession> {
    return this.http.post(endpoints.chats.claim(sessionId));
  }

  assign(sessionId: string, agentId: string): Promise<ChatSession> {
    return this.http.post(endpoints.chats.assign(sessionId), { agentId });
  }

  /** Owner/admin takes ownership of an already-claimed chat (403 for regular agents). */
  takeover(sessionId: string): Promise<ChatSession> {
    return this.http.post(endpoints.chats.takeover(sessionId));
  }

  /** Add a collaborating agent (role=joined) — both handle the chat; the claimer stays owner. */
  addParticipant(sessionId: string, agentId: string): Promise<ChatSession> {
    return this.http.post(endpoints.chats.participants(sessionId), { agentId });
  }

  close(sessionId: string): Promise<ChatSession> {
    return this.http.post(endpoints.chats.close(sessionId));
  }

  // ── Co-browse (agent side) ────────────────────────────────────────────────
  cobrowseStart(sessionId: string): Promise<{ ok: boolean }> {
    return this.http.post(endpoints.chats.cobrowseStart(sessionId));
  }

  cobrowseStop(sessionId: string): Promise<{ ok: boolean }> {
    return this.http.post(endpoints.chats.cobrowseStop(sessionId));
  }

  /** Latest DOM frame; pass `since` (last seen seq) to elide unchanged html. */
  cobrowseFrame(
    sessionId: string,
    since?: number,
  ): Promise<{ active: boolean; frame: CobrowseFrame | null }> {
    return this.http.get(endpoints.chats.cobrowseFrame(sessionId), {
      query: since != null ? { since } : undefined,
    });
  }

  /** Clear the unread counter when the agent opens the conversation. */
  markRead(sessionId: string): Promise<ChatSession> {
    return this.http.post(endpoints.chats.read(sessionId));
  }

  /**
   * Email the transcript. Omitting `to` lets the backend resolve the visitor's
   * email (pre-chat form / customer record); the server 422s when no recipient
   * can be resolved.
   */
  emailTranscript(sessionId: string, to?: string): Promise<{ ok: boolean }> {
    return this.http.post(endpoints.chats.emailTranscript(sessionId), to ? { to } : {});
  }

  // ── Tags + private notes (parity) ──────────────────────────────────────────
  /** Replace the conversation's tags (server dedupes/validates). */
  setTags(sessionId: string, tags: string[]): Promise<{ tags: string[] }> {
    return this.http.patch(endpoints.chats.tags(sessionId), { tags });
  }

  listNotes(sessionId: string): Promise<ChatNote[]> {
    return this.http.get(endpoints.chats.notes(sessionId));
  }

  addNote(sessionId: string, body: string): Promise<ChatNote> {
    return this.http.post(endpoints.chats.notes(sessionId), { body });
  }

  deleteNote(sessionId: string, noteId: string): Promise<void> {
    return this.http.delete(endpoints.chats.note(sessionId, noteId));
  }
}
